import React, { memo } from "react";
import { NavLink } from "react-router-dom";
import {
    Settings as SettingsIcon,
    Chat2 as ChatIcon,
    Folder as AdminIcon,
    Explore as AnalysisIcon,
    DocumentSuccess as RemediationIcon,
} from "../icons";
import { useUser } from "../../context/UserContext";
import { ADMIN, VIEW_ALL_CASES_ROLES, roleForSpace } from "../../config/roles";

// `allowedRoles` (when present) gates a button to those roles for the active
// search space, mirroring the backend can_* helpers in roles.py. Buttons with
// no `allowedRoles` are visible to every role.
const navItems = [
    { path: "/chat", label: "Chat", icon: ChatIcon },
    { path: "/admin", label: "Admin", allowedRoles: [ADMIN], icon: AdminIcon },
    {
        path: "/analysis_alt",
        label: "Analysis",
        allowedSpaces: ["in"],
        allowedRoles: VIEW_ALL_CASES_ROLES,
        icon: AnalysisIcon,
    },
    {
        path: "/remediation",
        label: "Remediation",
        allowedSpaces: ["in"],
        allowedRoles: VIEW_ALL_CASES_ROLES,
        icon: RemediationIcon,
    },
    {
        path: "/remediation-config",
        label: "Remediation Configuration",
        allowedSpaces: ["in"],
        allowedRoles: [ADMIN],
        icon: SettingsIcon,
    },
    { path: "/settings", label: "Settings", allowedRoles: [ADMIN], icon: SettingsIcon },
];

function SidebarRouteNav({ searchSpace, themeMode = "dark", onThemeModeChange }) {
    const normalizedThemeMode = themeMode === "light" ? "light" : "dark";
    const nextThemeMode = normalizedThemeMode === "light" ? "dark" : "light";

    const handleThemeToggle = () => {
        if (typeof onThemeModeChange === "function") {
            onThemeModeChange(nextThemeMode);
        }
    };
    const { roles } = useUser();
    const activeRole = roleForSpace(roles, searchSpace);

    return (
        <div
            style={{
                width: "100%",
                minWidth: 0,
                marginTop: "auto",
            }}
        >
            <div
                style={{
                    width: "100%",
                    borderTop: "1px solid var(--app-sidebar-divider)",
                    paddingTop: "16px",
                }}
            >
                {navItems
                    .filter((item) => !item.allowedSpaces || item.allowedSpaces.includes(searchSpace))
                        .filter((item) => !item.allowedRoles || item.allowedRoles.includes(activeRole))
                    .map((item) => {
                        const ItemIcon = item.icon;

                        return (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                end
                                className={({ isActive }) =>
                                    `chat-history-item-wrapper${isActive ? " is-active" : ""}`
                                }
                                style={{ textDecoration: "none" }}
                            >
                                <span
                                    className="chat-history-name"
                                    style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
                                >
                                    {ItemIcon && (
                                        <ItemIcon
                                            width={16}
                                            height={16}
                                            aria-hidden="true"
                                            style={{
                                                display: "block",
                                                color: "currentColor",
                                                fill: "currentColor",
                                                stroke: "currentColor",
                                                flexShrink: 0,
                                            }}
                                        />
                                    )}
                                    {item.label}
                                </span>
                            </NavLink>
                        );
                    })}

                <button
                    type="button"
                    className={`sidebar-theme-switch-button sidebar-theme-switch-button--${normalizedThemeMode}`}
                    onClick={handleThemeToggle}
                    aria-label={`Switch to ${nextThemeMode} mode`}
                    aria-pressed={normalizedThemeMode === "dark"}
                >
                    <span className="sidebar-theme-switch-track" aria-hidden="true">
                        <span className="sidebar-theme-switch-thumb" />
                    </span>

                    <span className="sidebar-theme-switch-label">
                        Dark/Light
                    </span>
                </button>
            </div>
        </div>
    );
}

export default memo(SidebarRouteNav);